/**
 * Prices that have moved since the estimate was quoted.
 *
 * The check against the quote compares an order with the prices the estimate
 * was generated at, frozen with it (ADR 0156), so a price-book edit afterwards
 * is not reported as something the order got wrong. That is right for the
 * order and wrong for the estimate: if a part now costs more than it did when
 * the customer was quoted, somebody should know, so they can requote before
 * the job loses the margin. Jonathon, 2026-09-18: "it should warn them so they
 * can requote."
 *
 * So this is a separate question with a separate answer. It never marks the
 * order as different; it says which quoted parts would cost what today, and
 * what that does to the job.
 */

import type { GeneratedLine, GeneratorProduct } from "./generator.js";
import { cents, multiplyCents, subCents, sumBy, type Cents } from "./money.js";
import type { QuoteDiscrepancy } from "./quoteCheck.js";
import type { PriceBook } from "./supplierPricing.js";

/** One quoted part whose price has moved since the quote. */
export interface QuotePriceMove {
  productId: string;
  description: string;
  /** The quoted quantity, which is what the move is costed over. */
  qty: number;
  /** Per unit, as quoted. */
  quoted: Cents;
  /** Per unit, from the same supplier today. */
  today: Cents;
  /** What the quoted quantity costs today less what it was quoted at. */
  effect: Cents;
}

export interface QuotePriceMoves {
  moves: QuotePriceMove[];
  /** Across every move: positive when the job now costs more than quoted. */
  effect: Cents;
}

/**
 * Compare each quoted part's price with what the same supplier charges today.
 *
 * `expected` is the check's own list (`compareOrderToQuote`), priced from the
 * estimate's frozen copy where it has one. `catalogue` and `prices` are today's
 * rows, never the frozen ones: the point is to see past the copy. A line prices
 * off its own supplier where it names one, and the generation supplier
 * otherwise, which is how it was quoted. A product no longer in the catalogue
 * has nothing to compare with and is left out, not reported as free.
 *
 * An estimate generated before anything was frozen was checked at today's
 * prices already, so it has no moves to report. Nor does an order that has
 * been placed: the materials are bought, and a price that moves afterwards is
 * nothing a requote can recover. The check compares prices only before placing
 * for the same reason.
 */
export function pricesMovedSinceQuote(input: {
  expected: readonly GeneratedLine[];
  catalogue: readonly GeneratorProduct[];
  prices: PriceBook;
  generationSupplierId: string | null;
  orderStatus?: string | null;
}): QuotePriceMoves {
  const moves: QuotePriceMove[] = [];
  if ((input.orderStatus ?? "not_placed") !== "not_placed") {
    return { moves, effect: cents(0) };
  }
  const byId = new Map(input.catalogue.map((p) => [p.id, p]));
  for (const line of input.expected) {
    const product = byId.get(line.product_id);
    if (!product) continue;
    const supplierId = line.supplierId ?? input.generationSupplierId;
    const today = input.prices.costOf(product, supplierId);
    if (today === line.unitPrice) continue;
    moves.push({
      productId: line.product_id,
      description: line.description,
      qty: line.qty,
      quoted: line.unitPrice,
      today,
      effect: subCents(
        multiplyCents(today, line.qty),
        multiplyCents(line.unitPrice, line.qty),
      ),
    });
  }
  return { moves, effect: sumBy(moves, (move) => move.effect) };
}

/**
 * The quoted part's move, when a price difference is the order being at
 * today's price for it.
 *
 * The check compares the order with the frozen quote, and the move compares
 * the quote with today, so a line re-picked, regenerated or copied at today's
 * price is both a price difference and a moved price: the same fact said
 * twice. ADR 0165 shows the move so the estimate can be requoted, not so the
 * order is pushed back to a price the supplier no longer charges. Null for
 * anything else, including a line at some third price.
 */
export function priceDifferenceAtToday(
  difference: QuoteDiscrepancy,
  priceMoves: QuotePriceMoves | null,
): QuotePriceMove | null {
  if (difference.kind !== "price" || difference.orderedPrice === null) {
    return null;
  }
  return (
    priceMoves?.moves.find(
      (move) =>
        move.productId === difference.productId &&
        move.today === difference.orderedPrice,
    ) ?? null
  );
}

/** What the quote check's correcting button does for one difference. */
export type QuoteFix =
  /** Put the line's quantity or price back to the quote's. */
  | "set_to_quoted"
  /** Put the quoted part back in place of the one ordered. */
  | "put_quoted_back"
  /** Take a line off the order that the quote never had. */
  | "remove"
  /** Add a quoted part nobody ordered. */
  | "add";

/**
 * Which correction, if any, the check offers for a difference.
 *
 * None when there is no single line to correct (a pooled quantity), when a
 * missing part has no quoted price to add it at, or when the order line is at
 * today's price for a part whose price moved since the quote: setting it to
 * quoted would write the stale price onto an unplaced order. That row is still
 * shown, and can still be kept. Warn only: nothing here blocks the order.
 */
export function quoteFixFor(
  difference: QuoteDiscrepancy,
  priceMoves: QuotePriceMoves | null,
): QuoteFix | null {
  switch (difference.kind) {
    case "not_ordered":
      return difference.quotedPrice !== null ? "add" : null;
    case "not_quoted":
      return difference.itemId !== null ? "remove" : null;
    case "swapped":
      return difference.itemId !== null ? "put_quoted_back" : null;
    case "qty":
      return difference.itemId !== null ? "set_to_quoted" : null;
    case "price":
      if (difference.itemId === null) return null;
      return priceDifferenceAtToday(difference, priceMoves)
        ? null
        : "set_to_quoted";
  }
}
